import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Tooltip
} from '@mui/material'
import SubjectIcon from '@mui/icons-material/Subject'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { getSectionHierarchyLevel } from '../../../utils/sectionExtract'

const TemplateContentsSidebar = ({ 
    templateData, 
    selectedSection, 
    onSectionClick,
    isCollapsed = false,
    onToggleCollapse
}) => {
    const [searchValue, setSearchValue] = useState('')
    const [sectionHierarchy, setSectionHierarchy] = useState([])
    const [expandedSections, setExpandedSections] = useState(new Set())

    const handleSearchChange = (event) => {
        setSearchValue(event.target.value)
    }

    const handleSearchKeyDown = (event) => {
        if (event.key === 'Escape') {
            setSearchValue('')
            event.target.blur()
        }
    }

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev)
            if (newSet.has(sectionId)) {
                newSet.delete(sectionId)
            } else {
                newSet.add(sectionId)
            }
            return newSet
        })
    }

    // Highlight matching text in search results
    const highlightMatch = (text, searchTerm) => {
        if (!searchTerm.trim()) return text
        
        const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
        const parts = text.split(regex)
        
        return parts.map((part, index) => {
            if (regex.test(part)) {
                return (
                    <span 
                        key={index} 
                        style={{ 
                            backgroundColor: '#FEF3C7',
                            fontWeight: 'bold'
                        }}
                    >
                        {part}
                    </span>
                )
            }
            return part
        })
    }

    // Build section hierarchy whenever filteredSections changes
    useEffect(() => {
        const buildHierarchy = () => {
            const hierarchy = []
            let currentParent = null
            
            const sections = templateData.filter((section) => {
                if (!searchValue.trim()) return true
                
                const searchLower = searchValue.toLowerCase().trim()
                const sectionTitle = section.title || section.section_name || ''
                const titleLower = sectionTitle.toLowerCase()
                
                // Search in title (which may include section number)
                if (titleLower.includes(searchLower)) return true
                
                // Extract and search section number from title (e.g., "1.2.3 Section Title")
                const numberMatch = sectionTitle.match(/^([\d.]+)\s/)
                if (numberMatch) {
                    const sectionNumber = numberMatch[1]
                    if (sectionNumber.includes(searchLower)) return true
                }
                
                // Search in section_id if it contains meaningful info
                if (section.section_id && section.section_id.toLowerCase().includes(searchLower)) {
                    return true
                }
                
                return false
            })
            
            sections.forEach((section, index) => {
                const sectionTitle = section.title || section.section_name || 'Untitled'
                const hierarchyLevel = getSectionHierarchyLevel(sectionTitle)
                
                if (hierarchyLevel === 0) {
                    // Save previous parent if exists
                    if (currentParent) {
                        hierarchy.push(currentParent)
                    }
                    // Create new parent
                    currentParent = {
                        id: section.section_id,
                        name: sectionTitle,
                        level: hierarchyLevel,
                        children: []
                    }
                } else if (currentParent) {
                    // Add as child to current parent
                    currentParent.children.push({
                        id: section.section_id,
                        name: sectionTitle,
                        level: hierarchyLevel
                    })
                }
                
                // Save last parent
                if (index === sections.length - 1 && currentParent) {
                    hierarchy.push(currentParent)
                }
            })
            
            setSectionHierarchy(hierarchy)
        }
        
        buildHierarchy()
    }, [templateData, searchValue])

    useEffect(() => {
        console.log('sectionHierarchy', sectionHierarchy)
    }, [sectionHierarchy])

    // Filter sections based on search
    const filteredSections = templateData.filter((section) => {
        if (!searchValue.trim()) return true
        
        const searchLower = searchValue.toLowerCase().trim()
        const sectionTitle = section.title || section.section_name || ''
        const titleLower = sectionTitle.toLowerCase()
        
        // Search in title (which may include section number)
        if (titleLower.includes(searchLower)) return true
        
        // Extract and search section number from title (e.g., "1.2.3 Section Title")
        const numberMatch = sectionTitle.match(/^([\d.]+)\s/)
        if (numberMatch) {
            const sectionNumber = numberMatch[1]
            if (sectionNumber.includes(searchLower)) return true
        }
        
        // Search in section_id if it contains meaningful info
        if (section.section_id && section.section_id.toLowerCase().includes(searchLower)) {
            return true
        }
        
        return false
    })

    // Render collapsed state
    if (isCollapsed) {
        return (
            <Box
                sx={{
                    width: '56px',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: '1px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                }}
            >
                <Tooltip title="Contents" placement="right" arrow>
                    <Box
                        onClick={onToggleCollapse}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#374151',
                            backgroundColor: 'transparent',
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: '#f3f4f6',
                                color: '#1F477D'
                            }
                        }}
                    >
                        <SubjectIcon sx={{ fontSize: '22px' }} />
                    </Box>
                </Tooltip>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                width: '20%',
                minWidth: '280px',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid #e5e7eb',
                overflow: 'hidden',
                backgroundColor: '#ffffff',
                scrollbarWidth: 'none', // Firefox
                boxSizing: 'border-box'
            }}
        >
            {/* Contents Header */}
            <Box
                sx={{
                    padding: '12px 8px 12px 16px',
                    backgroundColor: '#ffffff',
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#111827',
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        color: '#1F477D'
                    }}
                >
                    <SubjectIcon sx={{ fontSize: '18px' }} />
                    Contents
                </Typography>
                <IconButton
                    onClick={onToggleCollapse}
                    sx={{
                        // padding: '4px',
                        padding: 0,
                        color: '#9CA3AF',
                        size: '16px',
                        '&:hover': {
                            backgroundColor: 'transparent'
                        }
                    }}
                >
                    <KeyboardDoubleArrowLeftIcon sx={{ fontSize: '20px' }} />
                </IconButton>
            </Box>

            {/* Search Bar */}
            <Box
                sx={{
                    padding: '0px 8px 0px 8px',
                }}
            >
                <TextField
                    value={searchValue}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    slotProps={{
                        input: {
                            startAdornment: <SearchIcon sx={{ fontSize: '18px', color: '#9ca3af', padding: '0px' }} />,
                            endAdornment: searchValue && (
                                <IconButton
                                    onClick={() => setSearchValue('')}
                                    sx={{ 
                                        padding: '2px',
                                        '&:hover': {
                                            backgroundColor: 'transparent'
                                        }
                                    }}
                                >
                                    <ClearIcon sx={{ fontSize: '16px', color: '#9ca3af' }} />
                                </IconButton>
                            )
                        }
                    }}
                    placeholder="Search section number, title"
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            height: '32px',
                            fontSize: '12px',
                            lineHeight: '16px',
                            alignItems: 'center',
                            backgroundColor: '#f9fafb',
                            '& fieldset': {
                                borderColor: '#e5e7eb',
                            },
                            '&:hover fieldset': {
                                borderColor: '#d1d5db',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#3b82f6',
                                borderWidth: '1px',
                            },
                        },
                        '& .MuiOutlinedInput-root': {
                            paddingLeft: '10px',
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '4px 12px 4px 4px',
                            fontSize: '12px',
                            lineHeight: '16px',
                            alignItems: 'center',
                            height: '24px',
                            '&::placeholder': {
                                color: '#9ca3af',
                                opacity: 1,
                                fontSize: '12px',
                                lineHeight: '16px',
                                alignItems: 'center',
                            },
                        },
                    }}
                />
            </Box>

            {/* Search Results Count */}
            {searchValue && (
                <Box
                    sx={{
                        padding: '8px 16px',
                        borderBottom: '1px solid #e5e7eb',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '12px',
                            color: '#6b7280',
                        }}
                    >
                        {filteredSections.length} {filteredSections.length === 1 ? 'result' : 'results'} found
                    </Typography>
                </Box>
            )}

            {/* Scrollable Sections List */}
            <Box
                sx={{
                    overflow: 'auto',
                    flexGrow: 1,
                    padding: '8px',
                    // Styled scrollbar
                    scrollbarWidth: 'thin', // Firefox
                    scrollbarColor: '#888 #f1f1f1', // Firefox
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '4px',
                        '&:hover': {
                            background: '#555',
                        }
                    }
                }}
            >
                {filteredSections.length > 0 ? (
                    filteredSections.map((section) => {
                        const isSelected = selectedSection?.section_id === section.section_id
                        const hasContent = section.rules?.length > 0 || section.section_mode === 'dynamic'
                        const sectionTitle = section.title || section.section_name || 'Untitled'
                        const hierarchyLevel = getSectionHierarchyLevel(sectionTitle)

                        // Only render level 0 sections and their children (when expanded)
                        if (hierarchyLevel === 0) {
                            // Find children for this parent section
                            const parentIndex = filteredSections.indexOf(section)
                            const children = []
                            for (let i = parentIndex + 1; i < filteredSections.length; i++) {
                                const childSection = filteredSections[i]
                                const childTitle = childSection.title || childSection.section_name || 'Untitled'
                                const childLevel = getSectionHierarchyLevel(childTitle)
                                if (childLevel === 0) break // Hit next parent
                                children.push(childSection)
                            }

                            const hasChildren = children.length > 0
                            const isExpanded = expandedSections.has(section.section_id)

                            return (
                                <Box key={section.section_id || section.id}>
                                    {/* Parent Section */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '4px 10px 4px 8px',
                                            cursor: 'pointer',
                                            borderRadius: '6px',
                                            backgroundColor: isSelected ? '#EEF2FF' : 'transparent',
                                            border: '1px solid transparent',
                                            '&:hover': {
                                                backgroundColor: isSelected ? '#EEF2FF' : '#f9fafb',
                                            },
                                        }}
                                    >
                                        {/* Space for dropdown arrow - always reserve space */}
                                        <Box
                                            sx={{
                                                width: '24px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginRight: '4px',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {hasChildren && (
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        toggleSection(section.section_id)
                                                    }}
                                                    size="small"
                                                    sx={{
                                                        padding: '2px',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }}
                                                >
                                                    {isExpanded ? 
                                                        <ExpandMoreIcon sx={{ fontSize: '16px', color: '#6b7280' }} /> : 
                                                        <ChevronRightIcon sx={{ fontSize: '16px', color: '#6b7280' }} />
                                                    }
                                                </IconButton>
                                            )}
                                        </Box>
                                        
                                        {/* Section content - clickable area */}
                                        <Box
                                            onClick={() => onSectionClick(section)}
                                            sx={{
                                                flexGrow: 1,
                                                minWidth: 0, // Important for text ellipsis to work with flexbox
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    color: isSelected ? '#4338CA' : hasContent ? '#6B7280' : '#9ca3af',
                                                    fontWeight: hasContent ? 600 : 400,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {searchValue ? highlightMatch(sectionTitle, searchValue) : sectionTitle}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Children Sections (when expanded) */}
                                    {hasChildren && isExpanded && (
                                        <Box>
                                            {children.map((childSection) => {
                                                const childIsSelected = selectedSection?.section_id === childSection.section_id
                                                const childHasContent = childSection.rules?.length > 0 || childSection.section_mode === 'dynamic'
                                                const childTitle = childSection.title || childSection.section_name || 'Untitled'
                                                const childLevel = getSectionHierarchyLevel(childTitle)

                                                return (
                                                    <Box
                                                        key={childSection.section_id || childSection.id}
                                                        onClick={() => onSectionClick(childSection)}
                                                        sx={{
                                                            padding: '4px 10px 4px 8px',
                                                            paddingLeft: `${8 + 28 + (childLevel * 16)}px`, // 8px base + 28px for icon space + level indentation
                                                            cursor: 'pointer',
                                                            borderRadius: '6px',
                                                            backgroundColor: childIsSelected ? '#EEF2FF' : 'transparent',
                                                            border: '1px solid transparent',
                                                            overflow: 'hidden', // Important for text ellipsis
                                                            '&:hover': {
                                                                backgroundColor: childIsSelected ? '#EEF2FF' : '#f9fafb',
                                                            },
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: '14px',
                                                                color: childIsSelected ? '#4338CA' : childHasContent ? '#6B7280' : '#9ca3af',
                                                                fontWeight: childHasContent ? 600 : 400,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: 'block', // Ensure block display for ellipsis
                                                            }}
                                                        >
                                                            {searchValue ? highlightMatch(childTitle, searchValue) : childTitle}
                                                        </Typography>
                                                    </Box>
                                                )
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            )
                        }
                        
                        // Don't render non-level-0 sections at the top level (they'll be rendered as children)
                        return null
                    })
                ) : (
                    <Typography sx={{ padding: '12px', color: '#9ca3af', fontSize: '14px' }}>
                        {searchValue ? 'No sections found' : 'No sections available'}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

export default TemplateContentsSidebar